import { useMemo, useState } from 'react';
import { PhotoCaptureModal } from './PhotoCaptureModal';
import {
  useClientVisits,
  useClientPhotos,
  useClientPhotoUrls,
  createClientPhoto,
  deleteClientPhoto,
  updateClient,
  archiveClient,
  hardDeleteClient,
  restoreClient,
  type AcquisitionSource,
  type ClientRow,
  type ClientStatsView,
  type PhotoRow,
  type Tier,
} from '@busy/api';
import { Avatar, ModalShell, TierBadge, useToast } from '@busy/ui';
import { TextDraftPicker } from './TextDraftPicker';
import { SharePhotoModal } from './SharePhotoModal';
import { RevenueTierBadge } from './clients/RevenueTierBadge';
import { BigTipperBadge } from './clients/BigTipperBadge';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';
import {
  daysUntilBirthday,
  formatBirthdayShort,
} from '../lib/birthday';
import { BirthdayPicker } from './BirthdayPicker';
import { AcquisitionSourcePicker } from './AcquisitionSourcePicker';
import { useClientNoShows, useCustomServices } from '@busy/api';
import { CUT_ADDONS, CUT_TYPES } from '@busy/core';
import {
  aggregateClientTopPicks,
  matchesTopPick,
  type TopPick,
} from '../lib/clientTopPicks';

interface ClientDetailModalProps {
  client: ClientRow;
  stats: ClientStatsView | undefined;
  tier: Tier | undefined;
  onClose: () => void;
}

export function ClientDetailModal({ client, stats, tier, onClose }: ClientDetailModalProps) {
  const visits = useClientVisits(client.id);
  const photos = useClientPhotos(client.id);
  const customServices = useCustomServices();
  const toast = useToast();
  const t = useT();
  const language = useUI((s) => s.language);
  const locale = language === 'es' ? 'es-US' : 'en-US';

  // Most-requested service + add-on — aggregated from the client's own
  // visit history. Returns null when the client has <2 visits with any
  // one service/add-on (see MOST_REQUESTED_MIN_COUNT).
  const topPicks = useMemo(
    () => aggregateClientTopPicks(visits.data ?? []),
    [visits.data],
  );

  // Resolve a TopPick → display name. Checks the user's custom service
  // catalog first (in case they renamed a built-in), then falls back to
  // built-in CUT_TYPES / CUT_ADDONS for the localized name, then to the
  // raw normalized name as a last resort.
  const resolveName = (
    pick: TopPick | null,
    kind: 'service' | 'addon',
  ): string | null => {
    if (!pick) return null;
    const customs = customServices.data ?? [];
    if (pick.serviceId) {
      const custom = customs.find((s) => s.id === pick.serviceId);
      if (custom) return custom.name;
    }
    const catalog = kind === 'service' ? CUT_TYPES : CUT_ADDONS;
    const match = catalog.find((c) =>
      matchesTopPick(pick, {
        id: c.id,
        name: c.name,
        resolvedName: c.nameKey ? t(c.nameKey) : c.name,
      }),
    );
    if (match) return match.nameKey ? t(match.nameKey) : match.name;
    // Last resort — capitalize the normalized name (cut_type is stored
    // with whatever case the row was created with).
    if (pick.normalizedName) {
      return pick.normalizedName
        .split(' ')
        .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
        .join(' ');
    }
    return null;
  };

  const topServiceName = resolveName(topPicks.service, 'service');
  const topAddonName = resolveName(topPicks.addon, 'addon');

  // Most-recent photo for the header avatar — saves the initials fallback
  // when the client has uploaded reference shots.
  const latestPhoto = useMemo(() => {
    const list = (photos.data ?? [])
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return list[0] ?? null;
  }, [photos.data]);
  const headerPhotoUrls = useClientPhotoUrls(latestPhoto ? [latestPhoto] : []);
  const headerPhotoUrl = latestPhoto ? headerPhotoUrls[latestPhoto.id] : undefined;

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(client.first_name);
  const [lastName, setLastName] = useState(client.last_name);
  const [phone, setPhone] = useState(client.phone ?? '');
  // Birthday is split into month + day so the BirthdayPicker can wire
  // directly into native <select>s (iOS wheel, Android dropdown). The
  // picker keeps the pair in sync (clearing one clears the other), so
  // birthdayInvalid is just a defensive flag.
  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(
    client.birthday_month,
  );
  const [birthdayDay, setBirthdayDay] = useState<number | null>(
    client.birthday_day,
  );
  const birthdayInvalid =
    (birthdayMonth != null && birthdayDay == null) ||
    (birthdayMonth == null && birthdayDay != null);
  const [notes, setNotes] = useState(client.notes ?? '');
  // Acquisition source (migration 0075). Editable here so a barber can
  // backfill the channel for clients added before the picker existed,
  // or update if they remember new info.
  const [acquisitionSource, setAcquisitionSource] =
    useState<AcquisitionSource | null>(client.acquisition_source ?? null);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  const isArchived = client.archived_at != null;
  const [showTextDraft, setShowTextDraft] = useState(false);

  async function handleSave() {
    setSaving(true);
    const { error } = await updateClient(client.id, {
      first_name: firstName,
      last_name: lastName,
      phone: phone || null,
      notes: notes || null,
      birthday_month: birthdayMonth,
      birthday_day: birthdayDay,
      acquisition_source: acquisitionSource,
    });
    setSaving(false);
    if (error) {
      toast(t('clientDetail.saveFail', { message: error.message }), 'error');
    } else {
      setEditing(false);
      toast(t('clientDetail.saved'), 'success');
    }
  }

  async function handleArchive() {
    setArchiving(true);
    const { error } = await archiveClient(client.id);
    setArchiving(false);
    if (error) {
      toast(t('clientDetail.archiveFail', { message: error.message }), 'error');
    } else {
      toast(t('clientDetail.archived'), 'success');
      onClose();
    }
  }

  async function handleRestore() {
    setArchiving(true);
    const { error } = await restoreClient(client.id);
    setArchiving(false);
    if (error) {
      toast(t('clientDetail.restoreFail', { message: error.message }), 'error');
    } else {
      toast(t('clientDetail.restored'), 'success');
      onClose();
    }
  }

  async function handleHardDelete() {
    setArchiving(true);
    const { error } = await hardDeleteClient(client.id);
    setArchiving(false);
    if (error) {
      toast(t('clientDetail.hardDeleteFail', { message: error.message }), 'error');
    } else {
      toast(t('clientDetail.hardDeleted'), 'success');
      onClose();
    }
  }

  const name = `${client.first_name} ${client.last_name}`.trim() || t('today.unnamed');
  const fmtCents = (n: number | null | undefined) => `$${((n ?? 0) / 100).toFixed(0)}`;
  // No-shows for this client — interleaved into the visit history
  // timeline below so the barber sees the complete chronology
  // (visits + missed appointments) in one place. Hook is range-
  // independent (lifetime) and lightweight (one query keyed to
  // client_id).
  const noShows = useClientNoShows(client.id);
  const cycleDays = stats?.cycle_days ? Math.round(stats.cycle_days) : null;
  const daysSince = stats?.days_since_last_visit != null ? Math.floor(stats.days_since_last_visit) : null;
  const lastVisit = stats?.last_visit_at
    ? new Date(stats.last_visit_at).toLocaleDateString(locale)
    : null;

  // Cadence health — how this client is tracking against their own
  // average interval. Three buckets:
  //   < 0.85 × cycle → "On schedule" (green) — they're due-ish or
  //                    haven't passed their typical gap yet
  //   0.85–1.20 ×    → "Due soon" (amber) — within their normal
  //                    window, prime time to surface a rebook nudge
  //   > 1.20 ×       → "Overdue" (red) — past their typical gap,
  //                    increasingly at-risk
  // Only meaningful when we have BOTH a cycle (2+ visits) AND a
  // days-since reading. Otherwise we show a neutral status indicator
  // matching the user's current state (new / one-time).
  const cadenceHealth: {
    label: 'onSchedule' | 'dueSoon' | 'overdue' | 'newClient' | 'oneVisit';
    tone: 'green' | 'amber' | 'red' | 'neutral';
  } | null = (() => {
    if (cycleDays == null || daysSince == null) {
      // Single-visit clients: we know when they last came in but
      // there's no interval to compare against. Show "One visit so
      // far" — informative without faking a status.
      if ((stats?.visit_count ?? 0) === 1) {
        return { label: 'oneVisit', tone: 'neutral' };
      }
      // No visits at all — brand new client, encourage logging.
      return { label: 'newClient', tone: 'neutral' };
    }
    const ratio = daysSince / cycleDays;
    if (ratio < 0.85) return { label: 'onSchedule', tone: 'green' };
    if (ratio <= 1.2) return { label: 'dueSoon', tone: 'amber' };
    return { label: 'overdue', tone: 'red' };
  })();
  // Birthday pill in the header — only renders when on file. Shows "today",
  // "tomorrow", or "in N days" up to 30; beyond that just the short date so
  // the header doesn't get cluttered with "in 247 days." Deliberately
  // age-less — see migration 0062 header for the policy.
  const birthdayShort = formatBirthdayShort(
    client.birthday_month,
    client.birthday_day,
    language,
  );
  const birthdayDaysAway = daysUntilBirthday(
    client.birthday_month,
    client.birthday_day,
  );
  let birthdayBadge: string | null = null;
  if (birthdayShort && birthdayDaysAway != null) {
    if (birthdayDaysAway === 0) {
      birthdayBadge = t('clientDetail.birthday.today');
    } else if (birthdayDaysAway === 1) {
      birthdayBadge = t('clientDetail.birthday.tomorrow', { date: birthdayShort });
    } else if (birthdayDaysAway <= 30) {
      birthdayBadge = t('clientDetail.birthday.inDays', {
        n: birthdayDaysAway,
        date: birthdayShort,
      });
    } else {
      birthdayBadge = t('clientDetail.birthday.dateOnly', { date: birthdayShort });
    }
  }

  return (
    <ModalShell
      open
      onClose={onClose}
      size="xl"
      ariaLabel={t('clientDetail.aria', { name })}
    >
      {/* flex-col + max-h frame so the header pins to the top, the body
          scrolls between, and the footer (when editing) pins to the
          bottom — Save/Cancel buttons stay reachable no matter how long
          the visit history gets. */}
      <div className="flex flex-col max-h-[90dvh]">
      {/* Navy header — mirrors demo's openClientDetail (DEMO_REFERENCE.html:9814). */}
      <div className="shrink-0 bg-navy-900 text-white px-6 pt-7 pb-5 rounded-t-2xl relative">
        {/* Close X — top-right corner */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center text-white/70 hover:text-white hover:bg-white/15 border border-white/15 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Text button — gold pill, sits to the left of the close X. Greyed out
            when no phone is on file. Mirrors .cd-text-btn from the demo. */}
        <button
          type="button"
          onClick={() => client.phone && setShowTextDraft(true)}
          disabled={!client.phone}
          aria-label={client.phone ? t('clientDetail.textAria.has') : t('clientDetail.textAria.no')}
          title={client.phone ? t('clientDetail.textAria.has') : t('clientDetail.textAria.no')}
          className={`absolute top-3 right-14 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[13px] font-extrabold tracking-wide transition-all ${
            client.phone
              ? 'bg-gold-500 text-navy-900 hover:bg-gold-400 active:translate-y-px'
              : 'bg-white/10 text-white/45 cursor-not-allowed'
          }`}
        >
          <MessageIcon className="w-3.5 h-3.5" />
          <span>{t('clientDetail.textBtn')}</span>
        </button>
        {showTextDraft && (
          <TextDraftPicker
            client={client}
            stats={stats}
            onClose={() => setShowTextDraft(false)}
          />
        )}

        {/* Eyebrow — CLIENT */}
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mb-1.5">
          <UserIcon className="w-3 h-3" />
          {t('clientDetail.eyebrow')}
        </div>

        <div className="flex items-center gap-3">
          <Avatar name={name} size="lg" tier={tier} photoUrl={headerPhotoUrl} />
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h2 className="font-display text-2xl leading-tight">{name}</h2>
            {tier && <TierBadge tier={tier} size="sm" label={t(`tier.${tier}`)} />}
          </div>
        </div>

        <p className="text-sm text-white/70 mt-1.5">
          {stats && stats.visit_count > 0
            ? stats.visit_count === 1
              ? t('clientDetail.visit_one')
              : t('clientDetail.visit_other', { count: stats.visit_count })
            : t('clientDetail.visit_other', { count: 0 })}
          {stats?.revenue_tier && (
            <RevenueTierBadge tier={stats.revenue_tier} className="ml-3" />
          )}
          {stats?.is_big_tipper && <BigTipperBadge className="ml-2" />}
        </p>
        {client.phone && <p className="text-[12px] text-white/55 mt-1">{client.phone}</p>}
        {birthdayBadge && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold text-white/85">
            <CakeIcon className="w-3 h-3" />
            <span>{birthdayBadge}</span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
        {/* Stats Grid */}
        <section>
          <h3 className="font-display text-xs uppercase tracking-wide text-ink-500 mb-3">
            {t('clientDetail.section.stats')}
          </h3>
          {/* Cadence health pill — answers "are they due for a cut?"
              at a glance. Pairs the colored status with the actual
              numbers (e.g. "Due soon · 24d since · 28d avg") so the
              barber sees the reasoning without expanding anything. */}
          {cadenceHealth && (
            <div className="mb-3">
              <CadencePill
                kind={cadenceHealth.label}
                tone={cadenceHealth.tone}
                cycleDays={cycleDays}
                daysSince={daysSince}
              />
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <StatCard label={t('clientDetail.stat.visits')} value={stats?.visit_count ?? 0} />
            <StatCard
              label={t('clientDetail.stat.revenue')}
              value={fmtCents(stats?.total_revenue_cents)}
            />
            <StatCard
              label={t('clientDetail.stat.avgTicket')}
              value={fmtCents(stats?.avg_ticket_cents)}
            />
            <StatCard
              label={t('clientDetail.stat.cycle')}
              value={cycleDays ? `${cycleDays}d` : '—'}
            />
            <StatCard label={t('clientDetail.stat.lastVisit')} value={lastVisit ?? '—'} />
            <StatCard
              label={t('clientDetail.stat.daysSince')}
              value={daysSince != null ? `${daysSince}d` : '—'}
            />
          </div>
        </section>

        {/* Most Requested — surfaces only when the client has a clear
            favorite (2+ visits with the same service or add-on). Quiet
            empty state by default: when there isn't enough signal yet,
            the section just doesn't render. Mirrors the gold crown badge
            used in the cut-timer pickers so the visual vocabulary is
            consistent across surfaces. */}
        {(topServiceName || topAddonName) && (
          <section>
            <h3 className="font-display text-xs uppercase tracking-wide text-ink-500 mb-3">
              {t('clientDetail.section.mostRequested')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {topServiceName && topPicks.service && (
                <TopPickCard
                  label={t('clientDetail.mostRequested.service')}
                  name={topServiceName}
                  count={topPicks.service.count}
                  countLabel={t(
                    topPicks.service.count === 1
                      ? 'clientDetail.mostRequested.timesVisits_one'
                      : 'clientDetail.mostRequested.timesVisits_other',
                    { n: topPicks.service.count },
                  )}
                />
              )}
              {topAddonName && topPicks.addon && (
                <TopPickCard
                  label={t('clientDetail.mostRequested.addon')}
                  name={topAddonName}
                  count={topPicks.addon.count}
                  countLabel={t(
                    topPicks.addon.count === 1
                      ? 'clientDetail.mostRequested.timesAdded_one'
                      : 'clientDetail.mostRequested.timesAdded_other',
                    { n: topPicks.addon.count },
                  )}
                />
              )}
            </div>
          </section>
        )}

        {/* Photos */}
        <section>
          <h3 className="font-display text-xs uppercase tracking-wide text-ink-500 mb-1">
            {t('clientDetail.section.photos')}
          </h3>
          <p className="text-[11px] text-ink-500 leading-snug mb-3">
            {t('clientDetail.photos.help')}
          </p>
          {photos.loading && (
            <div className="grid grid-cols-3 gap-2">
              <div className="aspect-square rounded bg-bg-alt animate-pulse" />
            </div>
          )}
          {photos.error && <p className="text-sm text-red-600">{photos.error}</p>}
          {photos.data && (
            <PhotoGrid
              clientId={client.id}
              clientName={name}
              photos={photos.data}
              dateLocale={locale}
            />
          )}
        </section>

        {/* Visit History — interleaves logged visits with no-shows
            so the barber sees the complete chronology of this
            client's relationship. No-show entries render with an
            amber tint + "no-show" pill so they read distinctly
            from real visits. Sorted newest-first across both
            sources. */}
        <section>
          <h3 className="font-display text-xs uppercase tracking-wide text-ink-500 mb-3">
            {t('clientDetail.section.history')}
          </h3>
          {(visits.loading || noShows.loading) && (
            <p className="text-sm text-ink-500">{t('clientDetail.history.loading')}</p>
          )}
          {visits.error && <p className="text-sm text-red-600">{visits.error}</p>}
          {(() => {
            // Merged timeline — visits + no-shows interleaved by ts.
            // Built once per render; both arrays come pre-sorted
            // newest-first from their hooks, but the merge has to
            // sort again because the two streams are independent.
            const visitRows = (visits.data ?? []).map((v) => ({
              kind: 'visit' as const,
              id: v.id,
              ts: new Date(v.started_at).getTime(),
              data: v,
            }));
            const noShowRows = (noShows.data ?? []).map((n) => ({
              kind: 'noShow' as const,
              id: `noShow-${n.id}`,
              ts: new Date(n.scheduled_at).getTime(),
              data: n,
            }));
            const merged = [...visitRows, ...noShowRows].sort((a, b) => b.ts - a.ts);
            if (visits.loading || noShows.loading) return null;
            if (merged.length === 0) {
              return (
                <p className="text-sm text-ink-500">{t('clientDetail.history.empty')}</p>
              );
            }
            return (
              <ul className="divide-y divide-line">
                {merged.map((row) => {
                  if (row.kind === 'visit') {
                    const v = row.data;
                    const date = new Date(v.started_at).toLocaleDateString(locale);
                    const price = v.price_cents
                      ? `$${(v.price_cents / 100).toFixed(0)}`
                      : '—';
                    const tipCents = v.tip_cents ?? 0;
                    return (
                      <li
                        key={row.id}
                        className="flex justify-between items-center text-sm py-2 gap-3"
                      >
                        <div className="min-w-0">
                          <span className="font-medium">{date}</span>
                          {v.cut_type && (
                            <span className="text-ink-500 ml-2">· {v.cut_type}</span>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <span className="font-semibold">{price}</span>
                          {tipCents > 0 && (
                            <span className="ml-2 text-[11px] font-bold text-green-600 dark:text-green-400 tabular-nums">
                              {t('clientDetail.history.tipChip', {
                                amount: `$${(tipCents / 100).toFixed(
                                  tipCents % 100 === 0 ? 0 : 2,
                                )}`,
                              })}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  }
                  // No-show row — amber tint + pill for visual distinction
                  // from real visits. No price column since nothing was
                  // earned; the pill carries the "what is this" weight.
                  const n = row.data;
                  const date = new Date(n.scheduled_at).toLocaleDateString(locale);
                  return (
                    <li
                      key={row.id}
                      className="flex justify-between items-center text-sm py-2 gap-3"
                    >
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="font-medium text-ink-500">{date}</span>
                        <span className="text-[10px] uppercase tracking-[0.08em] font-extrabold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          {t('clientDetail.history.noShowPill')}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </section>

        {/* Editable Fields */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xs uppercase tracking-wide text-ink-500">
              {t('clientDetail.section.details')}
            </h3>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-sm text-navy-900 dark:text-gold-500 font-semibold hover:underline"
              >
                {t('clientDetail.edit')}
              </button>
            )}
          </div>
          <div className="space-y-3">
            <Field
              label={t('clientDetail.field.firstName')}
              value={firstName}
              onChange={setFirstName}
              disabled={!editing}
            />
            <Field
              label={t('clientDetail.field.lastName')}
              value={lastName}
              onChange={setLastName}
              disabled={!editing}
            />
            <Field
              label={t('clientDetail.field.phone')}
              value={phone}
              onChange={setPhone}
              disabled={!editing}
              type="tel"
            />
            {/* Optional birthday — month + day pickers. On iOS, each
                <select> opens the native scroll-wheel UI (matches the
                Contacts app), on Android opens a native dropdown sheet.
                Disabled state preserves the current values visibly so
                the barber can confirm what's on file without entering
                edit mode. */}
            <div>
              <label className="block text-xs font-semibold text-ink-500 mb-1">
                {t('clientDetail.field.birthday')}
              </label>
              <BirthdayPicker
                month={birthdayMonth}
                day={birthdayDay}
                onChange={(m, d) => {
                  setBirthdayMonth(m);
                  setBirthdayDay(d);
                }}
                disabled={!editing}
                language={language}
              />
              {editing && birthdayInvalid && (
                <p className="text-[11px] text-red-600 mt-1">
                  {t('newClient.birthday.invalid')}
                </p>
              )}
              {editing && !birthdayInvalid && (
                <p className="text-[11px] text-ink-500 mt-1">
                  {t('newClient.birthday.help')}
                </p>
              )}
            </div>
            <Field
              label={t('clientDetail.field.notes')}
              value={notes}
              onChange={setNotes}
              disabled={!editing}
              multiline
            />
            {/* Acquisition source — read-only when not editing (shows
                the current channel label or "—" if unspecified).
                Picker becomes interactive in edit mode for backfill. */}
            <div>
              <label className="block text-xs font-semibold text-ink-500 mb-1">
                {t('clientDetail.field.acquisitionSource')}
              </label>
              {editing ? (
                <AcquisitionSourcePicker
                  value={acquisitionSource}
                  onChange={setAcquisitionSource}
                />
              ) : (
                <div className="h-11 px-3 rounded-lg border border-line bg-bg-alt text-ink-700 flex items-center text-sm">
                  {acquisitionSource
                    ? t(`acquisitionSource.${acquisitionSource}`)
                    : '—'}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Archive / restore / permanently delete */}
        <div className="border-t border-line pt-4">
          {isArchived ? (
            confirmHardDelete ? (
              <div className="space-y-2">
                <p className="text-sm text-red-600 font-medium">
                  {t('clientDetail.hardDeleteConfirm', { name })}
                </p>
                <p className="text-xs text-ink-500 leading-relaxed">
                  {t('clientDetail.hardDeleteNote')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleHardDelete}
                    disabled={archiving}
                    className="px-4 h-9 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50"
                  >
                    {archiving ? t('clientDetail.hardDeleting') : t('clientDetail.hardDeleteYes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmHardDelete(false)}
                    className="px-4 h-9 rounded-lg border border-line bg-surface font-semibold"
                  >
                    {t('clientDetail.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-ink-500 leading-relaxed">
                  {t('clientDetail.archivedNote')}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleRestore}
                    disabled={archiving}
                    className="px-4 h-9 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
                  >
                    {archiving ? t('clientDetail.restoring') : t('clientDetail.restore')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmHardDelete(true)}
                    disabled={archiving}
                    className="px-4 h-9 rounded-lg border border-red-500/60 bg-surface text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-600/15 disabled:opacity-50"
                  >
                    {t('clientDetail.hardDelete')}
                  </button>
                </div>
              </div>
            )
          ) : !confirmArchive ? (
            <button
              type="button"
              onClick={() => setConfirmArchive(true)}
              className="text-sm text-red-600 font-semibold hover:underline"
            >
              {t('clientDetail.archive')}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-red-600 font-medium">
                {t('clientDetail.archiveConfirm', { name })}
              </p>
              <p className="text-xs text-ink-500 leading-relaxed">
                {t('clientDetail.archiveNote')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={archiving}
                  className="px-4 h-9 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50"
                >
                  {archiving ? t('clientDetail.archiving') : t('clientDetail.archiveYes')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmArchive(false)}
                  className="px-4 h-9 rounded-lg border border-line bg-surface font-semibold"
                >
                  {t('clientDetail.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Sticky footer — Save/Cancel pinned to the bottom of the modal
          when editing, so changing fields and saving never requires
          scrolling past the (often long) visit history. */}
      {editing && (
        <div className="shrink-0 border-t border-line bg-surface px-4 py-3 flex gap-2">
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setFirstName(client.first_name);
              setLastName(client.last_name);
              setPhone(client.phone ?? '');
              setBirthdayMonth(client.birthday_month);
              setBirthdayDay(client.birthday_day);
              setNotes(client.notes ?? '');
            }}
            className="px-6 h-11 rounded-lg border border-line bg-surface font-semibold"
          >
            {t('clientDetail.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || birthdayInvalid}
            className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
          >
            {saving ? t('clientDetail.saving') : t('clientDetail.save')}
          </button>
        </div>
      )}
      </div>
    </ModalShell>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  type?: string;
  multiline?: boolean;
}) {
  const base =
    'w-full px-3 rounded-lg border border-line bg-surface text-ink-900 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500';
  return (
    <div>
      <label className="block text-xs font-semibold text-ink-500 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={3}
          className={`${base} py-2 resize-none`}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`${base} h-10`}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-line bg-bg-alt p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-ink-500 mb-1">{label}</div>
      <div className="font-display text-lg text-ink-900">{value}</div>
    </div>
  );
}

/** Cadence health indicator — one of "on schedule / due soon / overdue
 *  / one visit / new client", colored by tone. Renders as a single
 *  full-width pill above the stats grid. Pairs the label with the raw
 *  numbers (days since × avg cycle) so the barber can verify the
 *  classification without trusting our copy alone. */
function CadencePill({
  kind,
  tone,
  cycleDays,
  daysSince,
}: {
  kind: 'onSchedule' | 'dueSoon' | 'overdue' | 'newClient' | 'oneVisit';
  tone: 'green' | 'amber' | 'red' | 'neutral';
  cycleDays: number | null;
  daysSince: number | null;
}) {
  const t = useT();
  const cls =
    tone === 'green'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-300'
      : tone === 'amber'
        ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700/50 dark:text-amber-300'
        : tone === 'red'
          ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-300'
          : 'bg-bg-alt border-line text-ink-700';
  const dotCls =
    tone === 'green'
      ? 'bg-emerald-500'
      : tone === 'amber'
        ? 'bg-amber-500'
        : tone === 'red'
          ? 'bg-red-500'
          : 'bg-ink-500/50';
  // Detail string varies by classification — the active states
  // (on-schedule / due-soon / overdue) compare days-since to the
  // average cycle; the new-client / one-visit states just summarize
  // what we know.
  let detail: string | null = null;
  if (kind === 'onSchedule' || kind === 'dueSoon' || kind === 'overdue') {
    if (daysSince != null && cycleDays != null) {
      detail = t('clientDetail.cadence.detail', {
        daysSince,
        cycleDays,
      });
    }
  } else if (kind === 'oneVisit') {
    detail = t('clientDetail.cadence.oneVisitDetail');
  }
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${cls}`}
      role="status"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${dotCls} shrink-0`} aria-hidden />
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-[12px] font-bold">
          {t(`clientDetail.cadence.${kind}`)}
        </div>
        {detail && (
          <div className="text-[10px] opacity-80 tabular-nums">{detail}</div>
        )}
      </div>
    </div>
  );
}

function TopPickCard({
  label,
  name,
  countLabel,
}: {
  label: string;
  name: string;
  count: number;
  countLabel: string;
}) {
  return (
    <div className="rounded-xl border border-gold-500/60 bg-gradient-to-br from-gold-500/10 to-transparent p-3">
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-extrabold text-navy-900 dark:text-gold-500 mb-1.5">
        <ClientDetailCrownIcon className="w-3 h-3" />
        {label}
      </div>
      <div className="font-display text-base text-ink-900 leading-tight truncate">
        {name}
      </div>
      <div className="text-[11px] text-ink-500 mt-0.5">{countLabel}</div>
    </div>
  );
}

function ClientDetailCrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" aria-hidden>
      <path d="M2 19h20l-2 2H4l-2-2z" />
      <path d="M3 7l4 5 5-8 5 8 4-5v10H3V7z" />
    </svg>
  );
}

// Mirrors demo's .cd-photo-grid (DEMO_REFERENCE.html:9770-9798): photo tiles +
// add tile that opens the camera on mobile (capture="environment"). Tiles show
// a date stamp; tap removes the photo after a confirm. Bucket is private so
// tiles render with signed URLs minted on mount.
function PhotoGrid({
  clientId,
  clientName,
  photos,
  dateLocale,
}: {
  clientId: string;
  clientName: string;
  photos: PhotoRow[];
  dateLocale: string;
}) {
  const t = useT();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [sharePhotoUrl, setSharePhotoUrl] = useState<string | null>(null);
  const urls = useClientPhotoUrls(photos);

  async function handleCaptured(blob: Blob) {
    setUploading(true);
    const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const result = await createClientPhoto(clientId, file);
    setUploading(false);
    setCaptureOpen(false);
    if (result.error) {
      toast(t('clientDetail.photos.uploadError', { message: result.error.message }), 'error');
    } else {
      toast(t('clientDetail.photos.added'), 'success');
    }
  }

  async function handleDelete(photo: PhotoRow) {
    if (!confirm(t('clientDetail.photos.deleteConfirm'))) return;
    const result = await deleteClientPhoto(photo.id, photo.storage_path);
    if (result.error) {
      toast(t('clientDetail.photos.deleteError', { message: result.error.message }), 'error');
    } else {
      toast(t('clientDetail.photos.removed'), 'success');
    }
  }

  const empty = photos.length === 0;

  return (
    <>
      {empty && (
        <p className="text-sm text-ink-500 mb-3">{t('clientDetail.photos.empty')}</p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p) => {
          const dateStr = new Date(p.created_at).toLocaleDateString(dateLocale, {
            month: 'short',
            day: 'numeric',
          });
          const url = urls[p.id];
          return (
            <div
              key={p.id}
              className="relative aspect-square rounded-lg overflow-hidden bg-bg-alt border border-line group"
            >
              {/* Tile is the share trigger. The delete button below it
                  stops propagation so the X doesn't accidentally open share. */}
              <button
                type="button"
                onClick={() => url && setSharePhotoUrl(url)}
                disabled={!url}
                aria-label={t('clientDetail.photos.shareAria')}
                className="absolute inset-0 w-full h-full"
              >
                {url ? (
                  <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-ink-500">
                    <CameraIcon className="w-6 h-6" />
                  </div>
                )}
              </button>
              <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-black/55 text-white text-[10px] font-bold tracking-wide pointer-events-none">
                {dateStr}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(p);
                }}
                aria-label={t('clientDetail.photos.deleteAria')}
                className="absolute top-1 right-1 w-6 h-6 rounded-full grid place-items-center bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => setCaptureOpen(true)}
          disabled={uploading}
          className={`relative aspect-square rounded-lg border-2 border-dashed border-line bg-bg-alt grid place-items-center text-ink-500 hover:border-navy-900 dark:hover:border-gold-500 hover:text-ink-700 transition-colors ${
            uploading ? 'opacity-60' : ''
          }`}
        >
          {uploading ? (
            <span className="text-[10px] font-bold tracking-wide uppercase">
              {t('clientDetail.photos.uploading')}
            </span>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <CameraIcon className="w-6 h-6" />
              <span className="text-[10px] font-bold tracking-wide uppercase">
                {t('clientDetail.photos.add')}
              </span>
            </div>
          )}
        </button>
      </div>

      <PhotoCaptureModal
        open={captureOpen}
        title={t('clientDetail.photos.addTitle')}
        onCapture={handleCaptured}
        onClose={() => setCaptureOpen(false)}
      />

      {sharePhotoUrl && (
        <SharePhotoModal
          imageUrl={sharePhotoUrl}
          clientName={clientName}
          clientId={clientId}
          shareTarget="gallery"
          onClose={() => setSharePhotoUrl(null)}
        />
      )}
    </>
  );
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="7" r="4" />
      <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
    </svg>
  );
}

function CakeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
      <path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
      <path d="M2 21h20" />
      <path d="M7 8v3" />
      <path d="M12 8v3" />
      <path d="M17 8v3" />
      <path d="M7 4v1" />
      <path d="M12 3v2" />
      <path d="M17 4v1" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
